import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type MouseEvent,
} from 'react';
import { TrashIcon } from '@phosphor-icons/react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { AutoHideOnScroll, PageTitle, toast } from '@/components';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { EngineService } from '@/services/engine';
import { fetchAllPages } from '@/services/engine/fetch-all-pages';
import { useGridMultiSelection, useOpenInNewWindow } from '@/hooks';
import { useCreateReport } from '@/hooks/useCreateReport';
import { useReportsFilters } from './hooks/useReportsFilters';
import { ReportsActionBar } from './components/ReportsActionBar';
import { ReportGridResults } from './components/ReportGridResults';
import { CreateReportModal } from '@/components/CreateReportModal';
import { DeleteReportConfirmationModal } from './components/DeleteReportConfirmationModal';
import { EditReportModal } from './components/EditReportModal';
import { FileSourceModal } from '@/features/NewSourceModal';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS, isReportsListCacheKey } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { PaginatedResponse } from '@/types/responses';
import type { ProcedureTemplate } from '@/types/procedure';
import type { ReportListItem } from '@/types/report';

const PAGE_SIZE = 12;
const REPORTS_REFRESH_INTERVAL_MS = 10_000;

type ReportsGridPageKey = readonly [string, string, string, string, number];

export function ReportGrid() {
    const { openUrl } = useOpenInNewWindow();
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { mutate: mutateCache } = useSWRConfig();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [searchDraft, setSearchDraft] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createReportTemplate, setCreateReportTemplate] =
        useState<ProcedureTemplate | null>(null);
    const [afterTemplateSourceUpload, setAfterTemplateSourceUpload] = useState<{
        reportId: string;
    } | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<
        { ids: string[] } | { described: true } | null
    >(null);
    const [isDeletingReport, setIsDeletingReport] = useState(false);
    const [reportPendingEdit, setReportPendingEdit] =
        useState<ReportListItem | null>(null);

    const { sortOption, setSortOption } = useReportsFilters();
    const orderBy = sortOption
        ? `${sortOption.field} ${sortOption.direction}`
        : '';

    const { data: procedureTemplates, error: procedureTemplatesError } = useSWR(
        CACHE_KEYS.PROCEDURE_TEMPLATES,
        () =>
            fetchAllPages((params) =>
                EngineService.getProcedureTemplates(params),
            ),
        {
            revalidateOnFocus: false,
        },
    );

    useEffect(() => {
        if (procedureTemplatesError) {
            toast.error(t('failedToLoadProcedureTemplates'));
        }
    }, [procedureTemplatesError, t]);

    const { data, isLoading, isValidating, error, size, setSize, mutate } =
        useSWRInfinite<
            PaginatedResponse<ReportListItem>,
            unknown,
            (
                pageIndex: number,
                previousPageData: PaginatedResponse<ReportListItem> | null,
            ) => ReportsGridPageKey | null
        >(
            (pageIndex, previousPageData) => {
                if (previousPageData && previousPageData.items.length === 0) {
                    return null;
                }
                return [
                    CACHE_KEYS.REPORTS_LIST,
                    'grid',
                    searchQuery,
                    orderBy,
                    pageIndex + 1,
                ] as const;
            },
            ([, , currentSearch, currentOrderBy, page]) =>
                EngineService.getReports({
                    page,
                    size: PAGE_SIZE,
                    search: currentSearch || undefined,
                    orderBy: currentOrderBy || undefined,
                }),
            {
                refreshInterval: REPORTS_REFRESH_INTERVAL_MS,
                revalidateFirstPage: true,
                revalidateOnFocus: false,
            },
        );

    const reports = useMemo(
        () => data?.flatMap((reportsPage) => reportsPage.items) ?? [],
        [data],
    );

    useEffect(() => {
        if (error) {
            toast.error(t('failedToLoad'));
        }
    }, [error, t]);

    const handleSearchSubmit = (value: string) => {
        if (value !== searchQuery) {
            setSearchQuery(value);
            void setSize(1);
        }
    };

    const handleSortOptionChange = (
        option: Parameters<typeof setSortOption>[0],
    ) => {
        setSortOption(option);
        void setSize(1);
    };

    const totalReports = data?.[0]?.page.totalItems ?? 0;
    const hasMore = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.page.page < lastPage.page.totalPages;
    }, [data]);
    const isInitialLoading = isLoading;
    const isLoadingMore =
        Boolean(data) && size > (data?.length ?? 0) && isValidating;

    const loadMore = useCallback(() => {
        if (hasMore && !isValidating) {
            void setSize((previous) => previous + 1);
        }
    }, [hasMore, isValidating, setSize]);

    const {
        selectedIds: selectedReportIds,
        isMultiSelectMode,
        selectedCount,
        toggleSelected: toggleReport,
        selectAll,
        selectAllMatching,
        isDescribed,
        isSelected,
        selection,
        clearSelection,
        exitMultiSelectMode,
        enterMultiSelectMode,
    } = useGridMultiSelection();

    const handleSelectAll = () => {
        if (selectedCount > 0 && selectedCount === totalReports) {
            clearSelection();
            return;
        }
        selectAllMatching({
            selectedAt: Math.floor(Date.now() / 1000),
            total: totalReports,
        });
    };

    const handleReportClick = (
        reportId: string,
        event?: MouseEvent<HTMLDivElement>,
    ) => {
        if (isMultiSelectMode) {
            toggleReport(reportId);
        } else {
            openUrl(generatePath(AppRoutes.REPORT, { reportId }), event, {
                preserveSearchParams: false,
            });
        }
    };

    const handleDeleteReportRequest = (reportId: string) => {
        setDeleteConfirm({ ids: [reportId] });
    };

    const handleDeleteSelected = () => {
        if (isDescribed) {
            setDeleteConfirm({ described: true });
            return;
        }
        if (selectedReportIds.size === 0) return;
        setDeleteConfirm({ ids: Array.from(selectedReportIds) });
    };

    const handleDeleteReportConfirm = async () => {
        if (!deleteConfirm || isDeletingReport) return;

        const described = 'described' in deleteConfirm;
        const isBulk = described || deleteConfirm.ids.length > 1;
        setIsDeletingReport(true);
        try {
            const {
                deleted: deletedCount,
                failed: failedCount,
                inProgress,
            } = await EngineService.deleteReports(
                described
                    ? {
                          selectAll: true,
                          selectedAt:
                              selection.mode === 'all'
                                  ? selection.selectedAt
                                  : Math.floor(Date.now() / 1000),
                          excludeIds:
                              selection.mode === 'all'
                                  ? Array.from(selection.excluded)
                                  : [],
                      }
                    : { ids: deleteConfirm.ids },
                described ? { search: searchQuery || undefined } : undefined,
            );

            await mutate();
            await mutateCache(isReportsListCacheKey);

            if (inProgress) {
                toast.info(t('deleteSelectedInProgress'));
            } else if (failedCount > 0 && deletedCount > 0) {
                toast.error(
                    t('deleteSelectedPartial', {
                        deleted: deletedCount,
                        failed: failedCount,
                    }),
                );
            } else if (failedCount > 0) {
                toast.error(t(isBulk ? 'deleteSelectedError' : 'deleteError'));
            } else {
                toast.success(
                    t(isBulk ? 'deleteSelectedSuccess' : 'deleteSuccess'),
                );
            }

            setDeleteConfirm(null);
            exitMultiSelectMode();
        } catch {
            toast.error(t(isBulk ? 'deleteSelectedError' : 'deleteError'));
        } finally {
            setIsDeletingReport(false);
        }
    };

    const selectionBulkActions: ActionConfig[] = [
        {
            type: ActionType.ITEM,
            icon: TrashIcon,
            label: t('actions.delete'),
            onClick: handleDeleteSelected,
        },
    ];

    const openCreateReportModal = useCallback(
        (selectedTemplate?: ProcedureTemplate) => {
            setCreateReportTemplate(selectedTemplate ?? null);
            setCreateModalOpen(true);
        },
        [],
    );

    const handleCreateModalClose = () => {
        setCreateModalOpen(false);
        setCreateReportTemplate(null);
    };

    const createReport = useCreateReport();

    const handleCreateReportSubmit = async (
        name: string,
        description: string | undefined,
        templateId?: string,
    ) => {
        const report = await createReport({
            name,
            description,
            templateId: templateId ?? undefined,
        });
        await setSize(1);
        await mutate();
        if (templateId) {
            queueMicrotask(() =>
                setAfterTemplateSourceUpload({ reportId: report.id }),
            );
        }
    };

    const handleEditReportModalClose = () => {
        setReportPendingEdit(null);
    };

    const handleEditReportSubmit = async (
        name: string,
        description: string | undefined,
    ) => {
        if (!reportPendingEdit) return;
        try {
            await EngineService.updateReport(reportPendingEdit.id, {
                name,
                description,
            });
            await mutate();
            toast.success(t('updateSuccess'));
        } catch (error) {
            toast.error(t('updateError'));
            throw error;
        }
    };

    const hasActiveQuery = searchQuery !== '';

    return (
        <div className="h-full flex flex-col relative">
            <AutoHideOnScroll container={scrollContainer}>
                <ReportsActionBar
                    isMultiSelectMode={isMultiSelectMode}
                    selectedCount={selectedCount}
                    totalCount={totalReports}
                    searchValue={searchDraft}
                    onSearchChange={setSearchDraft}
                    onSearchSubmit={handleSearchSubmit}
                    searchPlaceholder={t('searchPlaceholder')}
                    onExitMultiSelect={exitMultiSelectMode}
                    onSelectAll={handleSelectAll}
                    bulkActions={selectionBulkActions}
                    enterMultiSelectMode={enterMultiSelectMode}
                    sortOption={sortOption}
                    onSortOptionChange={handleSortOptionChange}
                    procedureTemplates={procedureTemplates ?? []}
                    onCreateReport={openCreateReportModal}
                />
            </AutoHideOnScroll>
            <div
                ref={setScrollContainer}
                className="flex-1 overflow-y-auto scrollbar-hide min-h-0 pb-20 px-4 panels:px-8"
            >
                <div className="w-full max-w-content mx-auto pt-20">
                    <div className="pt-4 pb-8">
                        <PageTitle
                            text={t('title')}
                            description={t('subtitle')}
                        />
                    </div>
                    <ReportGridResults
                        reports={reports}
                        isReportSelected={isSelected}
                        isMultiSelectMode={isMultiSelectMode}
                        onReportClick={handleReportClick}
                        onDeleteReport={handleDeleteReportRequest}
                        onEditReport={setReportPendingEdit}
                        onLoadMore={loadMore}
                        isLoadingMore={isLoadingMore}
                        hasMore={hasMore}
                        isInitialLoading={isInitialLoading}
                        scrollContainer={scrollContainer}
                        hasActiveQuery={hasActiveQuery}
                        procedureTemplates={procedureTemplates ?? []}
                        onCreateReport={openCreateReportModal}
                    />
                </div>
            </div>
            <CreateReportModal
                isOpen={createModalOpen}
                onClose={handleCreateModalClose}
                template={createReportTemplate}
                onSubmit={handleCreateReportSubmit}
            />
            <EditReportModal
                isOpen={reportPendingEdit !== null}
                onClose={handleEditReportModalClose}
                initialName={reportPendingEdit?.name ?? ''}
                initialDescription={reportPendingEdit?.description ?? ''}
                onSubmit={handleEditReportSubmit}
            />
            <FileSourceModal
                isOpen={afterTemplateSourceUpload !== null}
                reportId={afterTemplateSourceUpload?.reportId}
                onClose={() => setAfterTemplateSourceUpload(null)}
            />
            <DeleteReportConfirmationModal
                isOpen={deleteConfirm !== null}
                count={
                    deleteConfirm && 'described' in deleteConfirm
                        ? selectedCount
                        : deleteConfirm?.ids.length
                }
                deleteInProgress={isDeletingReport}
                onConfirm={handleDeleteReportConfirm}
                onCancel={() => setDeleteConfirm(null)}
            />
            <div
                className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
                style={{
                    background:
                        'linear-gradient(to top, rgb(17, 18, 20) 0%, transparent 100%)',
                }}
            />
        </div>
    );
}
